import React, { useState, useEffect, useCallback } from 'react';

/**
 * VisitorsPanel — shows real visitor traffic with BOTS separated from HUMANS.
 * Reused by the platform dashboard (scope = whole platform, with a per-tenant
 * breakdown) and each restaurant/store dashboard (scope = that tenant only).
 *
 * Props:
 *   fetchStats(days) -> Promise<{ data }>  — returns the visitor-stats payload
 *   accent    = brand color
 *   showTenants = true on the platform view (renders the per-tenant table)
 *   t         = optional label map
 */
export default function VisitorsPanel({ fetchStats, accent = '#c8a96e', showTenants = false, t = {} }) {
  const L = {
    title: 'Visitors', humans: 'Humans', bots: 'Bots', unique: 'Unique visitors',
    pageviews: 'Page views', botShare: 'Bot share', last7: '7 days', last30: '30 days', last90: '90 days',
    daily: 'Daily traffic (humans vs bots)', devices: 'Devices', countries: 'Top countries',
    topBots: 'Top bots & crawlers', topPages: 'Top pages', tenants: 'Per-tenant traffic',
    tenant: 'Tenant', empty: 'No visits recorded yet.', loading: 'Loading…',
    note: 'Bots (search engines, AI crawlers, scrapers) are counted separately from real people. Humans are counted server-side and de-duplicated with a first-party cookie.',
    ...t,
  };
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchStats(days).then(r => setStats(r.data || null)).catch(() => setStats(null)).finally(() => setLoading(false));
  }, [fetchStats, days]);
  useEffect(() => { load(); }, [load]);

  const card = { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.1)', borderRadius:14, padding:'1rem 1.15rem' };
  const muted = 'rgba(255,255,255,.45)';
  const fmt = (n) => (n == null ? '0' : Number(n).toLocaleString());

  const tot = stats?.totals || { humans:0, bots:0, uniqueHumans:0, pageViews:0 };
  const botShare = (tot.humans + tot.bots) > 0 ? Math.round(tot.bots / (tot.humans + tot.bots) * 100) : 0;
  const series = stats?.series || [];
  const maxDay = Math.max(1, ...series.map(d => d.humans + d.bots));

  const rangeBtn = (val, label) => (
    <button onClick={() => setDays(val)} style={{
      padding:'.4rem .8rem', borderRadius:8, cursor:'pointer', fontSize:'.8rem', fontWeight:700,
      border:`1px solid ${days===val ? accent : 'rgba(255,255,255,.14)'}`,
      background: days===val ? accent : 'transparent', color: days===val ? '#1a1a1a' : 'rgba(255,255,255,.6)',
    }}>{label}</button>
  );

  const Stat = ({ label, value, sub, color }) => (
    <div style={{ ...card, flex:'1 1 140px', minWidth:0 }}>
      <div style={{ fontSize:'.72rem', color:muted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
      <div style={{ fontSize:'1.7rem', fontWeight:800, color: color||'#fff', fontVariantNumeric:'tabular-nums', overflowWrap:'anywhere' }}>{value}</div>
      {sub && <div style={{ fontSize:'.72rem', color:muted }}>{sub}</div>}
    </div>
  );

  const Bars = ({ title, rows, total }) => (
    <div style={card}>
      <div style={{ fontWeight:700, color:'#fff', marginBottom:'.75rem' }}>{title}</div>
      {(!rows || rows.length === 0) ? <div style={{ color:muted, fontSize:'.85rem' }}>—</div> : rows.map((r, i) => {
        const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
        return (
          <div key={i} style={{ marginBottom:'.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.8rem', color:'rgba(255,255,255,.75)', gap:'.5rem' }}>
              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', minWidth:0 }}>{r.label}</span>
              <span style={{ flexShrink:0, fontVariantNumeric:'tabular-nums' }}>{fmt(r.count)}{total ? ` · ${pct}%` : ''}</span>
            </div>
            <div style={{ height:6, background:'rgba(255,255,255,.07)', borderRadius:4, marginTop:3 }}>
              <div style={{ width:`${pct}%`, height:'100%', background:r.color||accent, borderRadius:4 }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  const devices = stats?.devices || {};
  const deviceRows = ['mobile','desktop','tablet','unknown'].filter(k => devices[k]).map(k => ({ label: k, count: devices[k] }));

  return (
    <div>
      <div style={{ display:'flex', gap:'.5rem', alignItems:'center', flexWrap:'wrap', marginBottom:'1rem' }}>
        {rangeBtn(7, L.last7)}{rangeBtn(30, L.last30)}{rangeBtn(90, L.last90)}
        <button onClick={load} style={{ padding:'.4rem .8rem', borderRadius:8, border:'1px solid rgba(255,255,255,.14)', background:'transparent', color:'rgba(255,255,255,.6)', cursor:'pointer', fontSize:'.8rem', fontWeight:700 }}>↻</button>
      </div>

      <p style={{ fontSize:'.78rem', color:muted, marginBottom:'1rem', lineHeight:1.6 }}>{L.note}</p>

      {loading ? <div style={{ color:muted }}>{L.loading}</div> : (
        <>
          {/* Headline numbers */}
          <div style={{ display:'flex', gap:'.75rem', flexWrap:'wrap', marginBottom:'1rem' }}>
            <Stat label={L.humans} value={fmt(tot.uniqueHumans)} sub={`${fmt(tot.humans)} ${L.pageviews.toLowerCase()}`} color="#6ee7b7" />
            <Stat label={L.bots} value={fmt(tot.bots)} sub={`${botShare}% ${L.botShare.toLowerCase()}`} color="#c4b5fd" />
            <Stat label={L.pageviews} value={fmt(tot.pageViews)} />
          </div>

          {/* Daily stacked bars */}
          <div style={{ ...card, marginBottom:'1rem' }}>
            <div style={{ fontWeight:700, color:'#fff', marginBottom:'.75rem' }}>{L.daily}</div>
            {series.length === 0 ? <div style={{ color:muted, fontSize:'.85rem' }}>{L.empty}</div> : (
              <>
                <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:120, overflowX:'auto' }}>
                  {series.map((d, i) => (
                    <div key={i} title={`${d.date}: ${d.humans} humans, ${d.bots} bots`} style={{ flex:'1 0 5px', display:'flex', flexDirection:'column', justifyContent:'flex-end', height:'100%', minWidth:4 }}>
                      <div style={{ height:`${(d.bots/maxDay)*100}%`, background:'#c4b5fd', borderRadius:'2px 2px 0 0' }} />
                      <div style={{ height:`${(d.humans/maxDay)*100}%`, background:'#6ee7b7' }} />
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', gap:'1rem', marginTop:'.6rem', fontSize:'.75rem', color:muted }}>
                  <span><span style={{ display:'inline-block', width:10, height:10, background:'#6ee7b7', borderRadius:2, marginRight:5 }} />{L.humans}</span>
                  <span><span style={{ display:'inline-block', width:10, height:10, background:'#c4b5fd', borderRadius:2, marginRight:5 }} />{L.bots}</span>
                </div>
              </>
            )}
          </div>

          {/* Breakdowns */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:'.75rem', marginBottom:'1rem' }}>
            <Bars title={L.devices} total={tot.humans} rows={deviceRows} />
            <Bars title={L.countries} total={tot.humans} rows={(stats?.countries||[]).map(c => ({ label: c.cc, count: c.count }))} />
            <Bars title={L.topBots} total={tot.bots} rows={(stats?.topBots||[]).map(b => ({ label: b.name, count: b.count, color:'#c4b5fd' }))} />
            <Bars title={L.topPages} total={tot.humans} rows={(stats?.topPaths||[]).map(p => ({ label: p.path, count: p.count }))} />
          </div>

          {/* Per-tenant table (platform only) */}
          {showTenants && (
            <div style={card}>
              <div style={{ fontWeight:700, color:'#fff', marginBottom:'.75rem' }}>{L.tenants}</div>
              {(!stats?.tenants || stats.tenants.length === 0) ? <div style={{ color:muted, fontSize:'.85rem' }}>—</div> : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'.85rem' }}>
                    <thead><tr style={{ color:muted, textAlign:'left' }}>
                      <th style={{ padding:'.4rem .5rem' }}>{L.tenant}</th>
                      <th style={{ padding:'.4rem .5rem', textAlign:'right' }}>{L.humans}</th>
                      <th style={{ padding:'.4rem .5rem', textAlign:'right' }}>{L.bots}</th>
                    </tr></thead>
                    <tbody>
                      {stats.tenants.map(t2 => (
                        <tr key={t2.slug} style={{ borderTop:'1px solid rgba(255,255,255,.06)', color:'rgba(255,255,255,.85)' }}>
                          <td style={{ padding:'.45rem .5rem' }}>{t2.name || t2.slug}<span style={{ color:muted }}> · {t2.slug}</span></td>
                          <td style={{ padding:'.45rem .5rem', textAlign:'right', color:'#6ee7b7', fontVariantNumeric:'tabular-nums' }}>{fmt(t2.humans)}</td>
                          <td style={{ padding:'.45rem .5rem', textAlign:'right', color:'#c4b5fd', fontVariantNumeric:'tabular-nums' }}>{fmt(t2.bots)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
